// Connector update proposals — React Query hooks for the two review stages.
// Backend: services/api/src/routes/connectors.ts.
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiFetch, post, put } from "@/lib/api";
import { API_BASE } from "@/lib/utils";
import { queryClient } from "@/lib/query-client";
import { useAuth } from "@/lib/auth";

/**
 * 'connector' is reviewed by fastn platform_admin and patches the shared action.
 * 'workflow' is reviewed by the consuming org's owner and patches their own
 * workflow. Stage 2 only exists once stage 1 has applied.
 */
export type ProposalStage = "connector" | "workflow";

export type ProposalStatus =
  | "draft"
  | "pending_review"
  | "approved"
  | "applied"
  | "partially_applied"
  | "rejected"
  | "superseded";

/**
 * How much evidence backs a patch, weakest first.
 *
 * `spec_only` and `unverified` are NOT lesser shades of verified — they mean
 * nothing was executed against the vendor. The UI has to say so, because a
 * reasoned patch that renders like a proven one is the failure this whole
 * feature exists to prevent.
 */
export type VerificationTier =
  | "unverified"
  | "spec_only"
  | "traffic_verified"
  | "stub_verified"
  | "sandbox_verified";

/** Consumer counts for one org. */
export interface OrgImpact {
  orgId?: string;
  workflows: number;
  activeWorkflows: number;
  tenantPins: number;
  mappings: number;
  configs: number;
  widgets: number;
}

/** One case of the workflow's own suite, re-run against the patch. */
export interface SuiteCaseResult {
  id: string;
  name: string;
  status: "pass" | "fail" | "skipped";
  error?: string;
  /**
   * What went in, what the case says should happen, and what came back — as
   * text, ready to render.
   *
   * A tick means the case RAN without throwing; the `expected` line is prose
   * and nothing evaluates it. That gap is invisible on a patch that keeps the
   * contract and load-bearing on a repoint, where the criteria were written
   * against the retired one. So the reviewer gets all three and is the
   * assertion. Absent on older records, which is why every one is optional.
   */
  input?: string;
  expected?: string;
  output?: string;
}

export interface SuiteRun {
  mode: "mock";
  status: "pass" | "fail" | "partial" | "not_run";
  cases: SuiteCaseResult[];
  passed: number;
  failed: number;
  reason?: string;
  ranAt: string;
}

/** One of the three answers a patch verification gives. */
export interface VerificationCheck {
  status: "pass" | "fail" | "inconclusive" | "not_run";
  summary: string;
  details: string[];
}

/** services/api/src/lib/patch-verification — stored on `verification.checks`. */
export interface PatchVerification {
  version: 1;
  ranAt: string;
  verdict: "verified" | "failed" | "inconclusive";
  tier: VerificationTier;
  checks: { noRegression: VerificationCheck; intendedChange: VerificationCheck; liveRun: VerificationCheck };
  /** One per input the patch was run on, with that input's own answer. */
  fixtures: Array<{
    fixtureId: string;
    source: "test_case" | "recent_run";
    name: string;
    outcome?: "pass" | "fail" | "inconclusive";
    outcomeReason?: string;
    expected?: string;
    inputPreview?: string;
    /** What the workflow returned before the patch (or in production). */
    baselinePreview?: string;
    patchedPreview?: string;
    comparedAgainst?: "baseline" | "history" | "none";
  }>;
  withheld: string[];
  codeHash: string;
}

/** How the fix loop went for one workflow — stored on `verification.repair`. */
export interface RepairSummary {
  maxRounds: number;
  stoppedBecause: string;
  gaveUp: string | null;
  rounds: Array<{ round: number } & RepairRoundResult>;
}

/** services/api/src/lib/patch-verification/post-deploy.ts — stored on `verification.postDeploy`. */
export interface PostDeployWatch {
  status: "not_deployed" | "watching" | "healthy" | "regressed" | "quiet";
  summary: string;
  checkedAt: string;
  versionNumber: number | null;
  previousVersionId: string | null;
  previousVersionNumber: number | null;
  envIds: string[];
  runs: number;
  failed: number;
  sampleErrors: string[];
}

export interface ProposalItem {
  id: string;
  proposalId: string;
  itemKind: "action_patch" | "mapping_resolution" | "workflow_patch" | "tenant_pin" | "deprecation";
  targetType: string;
  targetId: string;
  before: unknown;
  after: unknown;
  /** What was run to prove this works. Null means nothing was. */
  verification: unknown;
  verificationTier: VerificationTier;
  /** Untrusted for agent-derived items — rendered as text, never as markup. */
  agentRationale: string | null;
  decision: "pending" | "accept" | "reject";
  decidedBy: string | null;
  decidedAt: string | null;
  note: string | null;
  appliedAt: string | null;
  applyError: string | null;
}

/** One workflow's result in one round of the fix loop. */
export interface RepairRoundResult {
  status: "pass" | "fail" | "inconclusive";
  passed: number;
  failed: number;
  total: number;
}

/**
 * A stage-2 repair running in the background (services/api/src/lib/patch-verification/repair-run-store.ts):
 * patch, verify every input, feed the failures back, patch again.
 */
export interface RepairRun {
  id: string;
  status: "running" | "succeeded" | "failed" | "interrupted";
  round: number;
  maxRounds: number;
  progress: {
    phase?: "writing" | "verifying" | "revising" | "saving";
    rounds?: Array<{ round: number; workflows: Record<string, RepairRoundResult> }>;
    stoppedBecause?: string | null;
    /** The run's agent session, open at /agent/:threadId from the moment the run starts. */
    threadId?: string;
  };
  error: string | null;
  startedAt: string;
  heartbeatAt: string;
  finishedAt: string | null;
}

export interface Proposal {
  id: string;
  orgId: string;
  connectorId: string | null;
  connectorSlug: string;
  changeEventId: string | null;
  stage: ProposalStage;
  status: ProposalStatus;
  /** Blast radius AS OF proposal time — snapshotted, not recomputed. */
  impact: {
    /** Present only for the org that OWNS the proposal. Every other reader gets
     *  `orgCount` instead: how widely a shared action is used is context they
     *  need, which tenants those are is not theirs. */
    orgs?: string[];
    orgCount?: number;
    workflows?: number;
    /** The subset of `workflows` that is published, enabled and not deleted. */
    activeWorkflows?: number;
    tenantPins?: number;
    mappings?: number;
    configs?: number;
    widgets?: number;
    consumers?: number;
    /** Per-org breakdown. Owning org (the platform reviewer) only. */
    byOrg?: OrgImpact[];
    /**
     * THIS reader's own estate, computed live rather than read off the snapshot.
     *
     * The snapshot answers "what did the platform reviewer decide against";
     * this answers "what do I have to fix", which is a present-tense question —
     * a workflow deleted last week must not be in it. Null means the proposal
     * predates this field, and the page says so rather than rendering it as
     * zero.
     */
    mine?: OrgImpact | null;
    truncated?: boolean;
  } | null;
  investigation: {
    source?: string;
    /** The session that produced this proposal's patches. */
    threadId?: string;
    /** Stage 1's session, kept as lineage once stage 2 recorded its own. */
    stage1ThreadId?: string;
    summary?: string;
    migration?: string;
    rationale?: string;
    confidence?: number;
    needsAgent?: boolean;
    /**
     * The action stage 1 retired, when the fix WAS the retirement.
     *
     * A stage-1 proposal that retires a dead endpoint legitimately holds zero
     * items — there is no contract to patch, the action is gone — and must
     * still apply, because applying is what releases the fan-out that moves
     * consumers onto the successor. Without this field a reader cannot tell
     * that case from "the agent produced nothing", and they read identically:
     * see the zero-items branch in ConnectorUpdatesPage.
     */
    retiredActionId?: string;
    openQuestions?: string[];
    recommendation?: string;
    refusedFields?: string[];
    adversarial?: { holds: boolean; objections: string[]; suggestedProbes: string[] };
  } | null;
  createdBy: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  appliedAt: string | null;
  error: string | null;
  /** The latest background repair run, for the reader's own stage-2 rows. */
  repairRun?: RepairRun | null;
  createdAt: string;
  items?: ProposalItem[];
  /**
   * How many items the proposal holds. The LIST read returns this instead of
   * the items themselves, which are a heavier, separate read — so a row that
   * has not been expanded yet has `itemCount` and no `items`, and the two must
   * never be confused: `items: undefined` means "not fetched", not "none".
   */
  itemCount?: number;
  /**
   * How many of those items carry a patch (not rejected, `after` set). A second
   * number rather than a flag off the first, because an item exists from the
   * moment a fan-out lands and stays empty until the agent fills it in — so
   * "there is an item" and "there is a fix" are independent, and the row's
   * button depends on the second.
   */
  writtenItemCount?: number;
  /** Only present on a stage-'connector' row. GET /connector-update-proposals
   *  scopes by org_id, so a reader's own request never returns another org's
   *  stage-2 (workflow) proposals to count client-side — this is computed
   *  server-side across that boundary instead. */
  stage2Summary?: { total: number; open: number };
}

export interface ApplyResult {
  applied: number;
  skipped: number;
  failed: number;
  refused: number;
  outcomes: Array<{ itemId: string; status: string; reason?: string }>;
  proposalStatus: "applied" | "partially_applied";
}

export interface FanOutResult {
  created: Proposal[];
  skipped: Array<{ orgId: string; consumerType: string; consumerId: string; reason: string }>;
  orgsConsidered: number;
  orgsDropped: number;
  /** The dependent scan hit its limit, so `orgsConsidered` is a floor and
   *  `orgsDropped` under-reports — some consumers were never even counted. */
  truncated?: boolean;
}

const listKey = (orgId?: string, stage?: ProposalStage, scope?: "mine") =>
  ["connector-updates", orgId, stage ?? "all", scope ?? "default"] as const;
const detailKey = (orgId: string | undefined, id: string) =>
  ["connector-updates", orgId, "detail", id] as const;

/**
 * `scope: "mine"` asks for only the updates touching something this workspace
 * calls, whatever the caller's role. Without it a platform_admin gets every
 * proposal on the platform, which is the reviewer's view, not the workspace's.
 */
export function useProposals(opts?: { stage?: ProposalStage; status?: string; scope?: "mine"; enabled?: boolean }) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const params = new URLSearchParams();
  if (opts?.stage) params.set("stage", opts.stage);
  if (opts?.status) params.set("status", opts.status);
  if (opts?.scope) params.set("scope", opts.scope);
  const qs = params.toString() ? `?${params}` : "";
  return useQuery({
    queryKey: listKey(orgId, opts?.stage, opts?.scope),
    queryFn: () => apiFetch<Proposal[]>(`${API_BASE}/v1/connector-update-proposals${qs}`),
    enabled: !!orgId && (opts?.enabled ?? true),
    // A background repair reports through its row; poll only while one runs.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((p) => p.repairRun?.status === "running") ? 5_000 : false,
  });
}

export function useProposal(id: string | null) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useQuery({
    queryKey: detailKey(orgId, id ?? ""),
    queryFn: () =>
      apiFetch<Proposal>(`${API_BASE}/v1/connector-update-proposals/${encodeURIComponent(id!)}?scope=mine`),
    enabled: !!orgId && !!id,
    refetchInterval: (query) => (query.state.data?.repairRun?.status === "running" ? 4_000 : false),
  });
}

/** Turn an accepted signal into a stage-1 proposal. */
export function useProposeFix() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useMutation({
    mutationFn: (changeEventId: string) =>
      apiFetch<{ proposal: Proposal; needsAgent: boolean; rationale: string }>(
        `${API_BASE}/v1/connector-changes/${encodeURIComponent(changeEventId)}/propose`,
        post({}),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connector-updates", orgId] });
      queryClient.invalidateQueries({ queryKey: ["connector-changes", orgId] });
    },
  });
}

/** Run the repair agent against an existing proposal. Slow by design. */
export function useRunAgent() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useMutation({
    // Stage 1 answers with the finished proposal; stage 2 answers 202 with the
    // background run it started, which the list and detail then poll.
    mutationFn: ({ id, vendorDocs }: { id: string; vendorDocs?: string }) =>
      apiFetch<{ recommendation?: string; proposal?: Proposal; stage?: string; run?: RepairRun }>(
        `${API_BASE}/v1/connector-update-proposals/${encodeURIComponent(id)}/agent-run`,
        post({ vendorDocs }),
      ),
    // Settled, not success: a 409 means a run is already going, and the reads
    // are what show it.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["connector-updates", orgId] }),
  });
}

/**
 * Decide one item. Per item, not per proposal: a reviewer who accepts three of
 * five has approved three, and apply honours exactly that.
 */
export function useDecideItem() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useMutation({
    mutationFn: ({ proposalId, itemId, decision, after, note, overrideReason }: {
      proposalId: string;
      itemId: string;
      decision: "accept" | "reject";
      after?: unknown;
      note?: string;
      /** Required by the API to accept a patch whose verification failed. */
      overrideReason?: string;
    }) =>
      apiFetch<ProposalItem>(
        `${API_BASE}/v1/connector-update-proposals/${encodeURIComponent(proposalId)}/items/${encodeURIComponent(itemId)}`,
        put({ decision, after, note, overrideReason }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connector-updates", orgId] }),
  });
}

export function useApplyProposal() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ApplyResult>(
        `${API_BASE}/v1/connector-update-proposals/${encodeURIComponent(id)}/apply`,
        post({}),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connector-updates", orgId] });
      // Applying resolves the change event and moves the health verdict.
      queryClient.invalidateQueries({ queryKey: ["connector-changes", orgId] });
      queryClient.invalidateQueries({ queryKey: ["integrations", orgId, "health"] });
    },
  });
}

/** Stage 2: fan an applied stage-1 fix out to every consuming org. */
export function useFanOut() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<FanOutResult>(
        `${API_BASE}/v1/connector-update-proposals/${encodeURIComponent(id)}/fan-out`,
        post({}),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connector-updates", orgId] }),
  });
}

/**
 * The agent session behind this update, when the viewer can open it.
 *
 * The run's own session first — it exists from the moment a repair starts. Then
 * the proposal's: a stage-1 proposal's thread is its own org's, but the fan-out
 * copies stage 1's thread into every consuming org's proposal, and that one
 * lives in the platform org, where the reviewer is not a member. It is only
 * theirs once stage 2 has written its own, which is when stage 1's moves to
 * `stage1ThreadId`.
 */
export function sessionThreadOf(proposal: Pick<Proposal, "stage" | "investigation" | "repairRun">): string | null {
  const runThread = proposal.repairRun?.progress?.threadId;
  if (runThread) return runThread;
  const inv = proposal.investigation;
  if (!inv?.threadId) return null;
  return proposal.stage === "connector" || inv.stage1ThreadId ? inv.threadId : null;
}
